import React from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Pagination,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { List, ListItem, ListItemText, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { getShoppingCartRules } from "app/services/api/shoppingCart";

const DIGITS = "0123456789";
const MAX_LEN = 6;
const PAGE_SIZE = 10;

const CodesTab = ({ initialCodes = [], onChange, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState("");
  const [codes, setCodes] = React.useState([]);
  const [bulkCount, setBulkCount] = React.useState(1);
  const [bulkError, setBulkError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [conflictOpen, setConflictOpen] = React.useState(false);
  const [conflictTitle, setConflictTitle] = React.useState("");
  const [conflictMessage, setConflictMessage] = React.useState("");

  const sanitize = (val) =>
    String(val || "")
      .replace(/[^0-9]/g, "")
      .slice(0, MAX_LEN);

  const validate = (val) => {
    if (!val) {
      return t("validation.required", { defaultValue: "Required" });
    }
    if (!/^[0-9]{1,6}$/.test(val)) {
      return t("validation.invalid", {
        defaultValue: "Only digits, max 6",
      });
    }
    return "";
  };

  const normalizeKey = React.useCallback(
    (val) => String(val || "").trim().toUpperCase(),
    []
  );

  const isActiveCode = React.useCallback((c) => {
    if (!c) return false;
    if (typeof c.isActive === "boolean") return c.isActive;
    if (c.status) return String(c.status).toLowerCase() === "active";
    // Backward-compatible default: treat missing status as active
    return true;
  }, []);

  const loadActiveCodesAcrossAllRules = React.useCallback(async () => {
    try {
      const rules = await getShoppingCartRules();
      const active = new Set();
      (Array.isArray(rules) ? rules : []).forEach((rule) => {
        const list = rule?.codes;
        (Array.isArray(list) ? list : []).forEach((c) => {
          if (!isActiveCode(c)) return;
          const key = normalizeKey(c?.code);
          if (key) active.add(key);
        });
      });
      return active;
    } catch (_e) {
      return new Set();
    }
  }, [isActiveCode, normalizeKey]);

  const handleChangeCode = (e) => {
    const value = sanitize(e.target.value);
    setCode(value);
    setError(validate(value));
  };

  const randomCode = () => {
    let result = "";
    for (let i = 0; i < MAX_LEN; i++) {
      result += DIGITS.charAt(Math.floor(Math.random() * DIGITS.length));
    }
    return result;
  };

  // Single manual code generation:
  // - If collision with an ACTIVE code anywhere across customer's cart rules, STOP and show a modal.
  const generateCode = async () => {
    const candidate = randomCode();
    const activeAcrossRules = await loadActiveCodesAcrossAllRules();
    const key = normalizeKey(candidate);
    if (activeAcrossRules.has(key)) {
      setConflictTitle(
        t("pages.dsokCartRules.codeConflict.title", {
          defaultValue: "Code already exists",
        })
      );
      setConflictMessage(
        t("pages.dsokCartRules.codeConflict.generateMessage", {
          defaultValue:
            "This discount code already exists and is active. Please generate a different code.",
        })
      );
      setConflictOpen(true);
      return;
    }
    setCode(candidate);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate(code);
    setError(err);
    if (err) return;

    // Final safety net on Add: prevent inserting a code that exists as ACTIVE in any cart rule
    const activeAcrossRules = await loadActiveCodesAcrossAllRules();
    const key = normalizeKey(code);
    if (key && activeAcrossRules.has(key)) {
      setConflictTitle(
        t("pages.dsokCartRules.codeConflict.title", {
          defaultValue: "Code already exists",
        })
      );
      setConflictMessage(
        t("pages.dsokCartRules.codeConflict.addMessage", {
          defaultValue:
            "This discount code already exists and is active. The code was not added.",
        })
      );
      setConflictOpen(true);
      // keep input intact so user can adjust it
      return;
    }

    const exists = (codes || []).some(
      (c) => normalizeKey(c?.code) === normalizeKey(code)
    );
    if (exists) {
      setError(
        t("validation.duplicate", { defaultValue: "Code already exists" })
      );
      return;
    }
    const next = [...codes, { code, isActive: true }];
    setCodes(next);
    typeof onChange === "function" && onChange(next);
    // Reset form
    setCode("");
    setError("");
  };

  const handleToggle = (idx) => {
    setCodes((prev) => {
      const next = prev.map((item, i) =>
        i === idx ? { ...item, isActive: !Boolean(item.isActive) } : item
      );
      typeof onChange === "function" && onChange(next);
      return next;
    });
  };

  const handleRemove = (idx) => {
    setCodes((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      typeof onChange === "function" && onChange(next);
      return next;
    });
  };

  React.useEffect(() => {
    if (Array.isArray(initialCodes) && initialCodes.length) {
      setCodes(
        (initialCodes || []).map((c) => ({
          ...c,
          isActive:
            typeof c.isActive === "boolean"
              ? c.isActive
              : c.status
              ? String(c.status).toLowerCase() === "active"
              : true,
        }))
      );
    }
  }, [initialCodes]);

  const handleBulkGenerate = async () => {
    let count = parseInt(bulkCount, 10);
    if (isNaN(count) || count < 1) count = 1;
    if (count > 50) count = 50;

    // Bulk generation:
    // - silently retries on collisions with ACTIVE codes across all customer's cart rules
    // - also avoids duplicates within this rule (case-insensitive)
    const activeAcrossRules = await loadActiveCodesAcrossAllRules();
    const existing = new Set((codes || []).map((c) => normalizeKey(c?.code)));
    const newItems = [];
    while (newItems.length < count) {
      let attempts = 0;
      let placed = false;
      while (attempts < 2000 && !placed) {
        const c = randomCode();
        const key = normalizeKey(c);
        if (!key) {
          attempts += 1;
          continue;
        }
        if (existing.has(key) || activeAcrossRules.has(key)) {
          // Collision: do NOT notify user, just retry
          attempts += 1;
          continue;
        }
        existing.add(key);
        newItems.push({ code: c, isActive: true });
        placed = true;
      }
      if (!placed) {
        // Extremely unlikely (exhausted space / too many collisions)
        setBulkError(
          t("validation.couldNotGenerate", {
            defaultValue: "Could not generate requested number of unique codes.",
          })
        );
        break;
      }
    }
    const next = [...codes, ...newItems];
    setCodes(next);
    typeof onChange === "function" && onChange(next);
    if (newItems.length === count) setBulkError("");
  };

  // Ensure current page is valid when codes change
  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil((codes || []).length / PAGE_SIZE));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [codes, page]);

  return (
    <Box sx={{ p: 3 }} component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("pages.dsokCartRules.codesTabTitle", { defaultValue: "Codes" })}
      </Typography>
      <Stack
        spacing={2}
        direction={{ xs: "column", sm: "row" }}
        alignItems="flex-start"
      >
        <TextField
          label={t("pages.dsokCartRules.code", { defaultValue: "Code" })}
          placeholder={t("pages.dsokCartRules.codePlaceholder", {
            defaultValue: "123456",
          })}
          value={code}
          onChange={handleChangeCode}
          error={Boolean(error)}
          helperText={
            error ||
            t("pages.dsokCartRules.codeHelp", {
              defaultValue: "Up to 6 digits",
            })
          }
          inputProps={{ maxLength: MAX_LEN }}
          size="small"
        />
        <Button variant="outlined" onClick={generateCode} size="small">
          {t("pages.dsokCartRules.generateCode", { defaultValue: "Generate" })}
        </Button>
        <Button variant="contained" type="submit" size="small">
          {t("buttons.add", { defaultValue: "Add" })}
        </Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <TextField
          label={t("pages.dsokCartRules.bulkCount", {
            defaultValue: "Max count",
          })}
          type="number"
          value={bulkCount}
          onChange={(e) => {
            const val = e.target.value;
            const num = parseInt(val, 10);
            if (isNaN(num)) {
              setBulkCount("");
              setBulkError("");
              return;
            }
            if (num > 50) {
              setBulkCount(50);
              setBulkError(t("validation.max50", { defaultValue: "Max 50" }));
            } else if (num < 1) {
              setBulkCount(1);
              setBulkError("");
            } else {
              setBulkCount(num);
              setBulkError("");
            }
          }}
          inputProps={{ min: 1, max: 50 }}
          error={Boolean(bulkError)}
          helperText={
            bulkError ||
            t("pages.dsokCartRules.bulkHelp", {
              defaultValue: "Generate up to 50 active codes",
            })
          }
          size="small"
        />
        <Button variant="contained" onClick={handleBulkGenerate} size="small">
          {t("pages.dsokCartRules.bulkGenerate", {
            defaultValue: "Generate",
          })}
        </Button>
      </Stack>
      <Box sx={{ mt: 3 }}>
        <TextField
          fullWidth
          size="small"
          label={t("pages.dsokCartRules.searchCodes", {
            defaultValue: "Search codes",
          })}
          placeholder={t("pages.dsokCartRules.searchCodesPlaceholder", {
            defaultValue: "Type code...",
          })}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        {(() => {
          const q = (searchQuery || "").trim().toUpperCase();
          const filtered = q
            ? (codes || []).filter((item) =>
                (item.code || "").toUpperCase().includes(q)
              )
            : codes || [];
          const totalPages = Math.max(
            1,
            Math.ceil(filtered.length / PAGE_SIZE)
          );
          const baseIndex = (page - 1) * PAGE_SIZE;
          const visibleCodes = filtered.slice(baseIndex, baseIndex + PAGE_SIZE);
          return (
            <>
              <List dense>
                {visibleCodes.map((item, idx) => (
                  <ListItem
                    key={`${item.code}-${codes.indexOf(item)}`}
                    secondaryAction={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(item.isActive)}
                              onChange={() => handleToggle(codes.indexOf(item))}
                            />
                          }
                          label={t("common.active", { defaultValue: "Active" })}
                        />
                        <IconButton
                          edge="end"
                          onClick={() => handleRemove(codes.indexOf(item))}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={item.code}
                      secondary={
                        t("common.active", { defaultValue: "Active" }) +
                        ": " +
                        (item.isActive
                          ? t("common.yes", { defaultValue: "Yes" })
                          : t("common.no", { defaultValue: "No" }))
                      }
                    />
                  </ListItem>
                ))}
              </List>
              {totalPages > 1 && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    size="small"
                  />
                </Stack>
              )}
            </>
          );
        })()}
      </Box>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 2 }}
      >
        {onCancel && (
          <Button variant="outlined" color="inherit" onClick={onCancel}>
            {t("buttons.cancel", { defaultValue: "Cancel" })}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={() => {
            typeof onSave === "function" && onSave({ codes });
          }}
        >
          {t("buttons.save", { defaultValue: "Save" })}
        </Button>
      </Stack>

      <Dialog open={conflictOpen} onClose={() => setConflictOpen(false)}>
        <DialogTitle>
          {conflictTitle ||
            t("pages.dsokCartRules.codeConflict.title", {
              defaultValue: "Code already exists",
            })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">{conflictMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setConflictOpen(false)}>
            {t("common.ok", { defaultValue: "OK" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CodesTab;
